import React, { useEffect, useRef, useState } from 'react';
import {
    ArrowLeft, PlayCircle, Video, MousePointerClick, Keyboard, Eye, Hourglass,
    Lightbulb, AlertTriangle, HelpCircle, CheckCircle2, MessageCircleQuestion, FlaskConical, Clock,
} from 'lucide-react';
import type { TutorialAktion, TutorialKapitel, TutorialKapitelId } from '../../tutorial/types';
import type { KapitelDaten } from '../../services/tutorial/tutorialService';
import { BEREICH_LABEL } from '../../services/tutorial/tutorialLogic';

/**
 * LERNZENTRUM — eine Kapitelseite (Plan §2.3).
 *
 * Reine Darstellung: sie laedt nichts, schreibt nichts und kennt die
 * Navigation nur ueber Rueckkanaele. Alles, was sie zeigt, kommt entweder aus
 * dem Register (immer da) oder aus `tutorial_kapitel` (kann fehlen).
 *
 * DIE REGEL DIESER SEITE: jedes Feld aus der Datenbank hat einen Rueckfall auf
 * das Register. Solange die Migration und die Pipeline nicht gelaufen sind,
 * steht hier trotzdem eine vollstaendige Anleitung — nur ohne Video und ohne
 * Screenshots. Eine Hilfe, die erst nach einem Deployment hilft, ist keine.
 */

interface Props {
    kapitel: TutorialKapitel;
    /** Aus `tutorial_kapitel`; fehlt, solange nichts freigegeben ist. */
    daten?: KapitelDaten;
    /** Kuratiert aus `stolperfallen.md` (Loader) — ergaenzt die des Registers. */
    stolperfallen: string[];
    abgeschlossen: boolean;
    onZurueck: () => void;
    /** „Jetzt in der App zeigen" — Tour-Overlay folgt in Paket B. */
    onStarteTour: (kapitelId: TutorialKapitelId) => void;
    /** „Selbst ausprobieren" — Uebungsplatz (Auftrag A4). Fehlt er, entfaellt der Knopf. */
    onSelbstAusprobieren?: (kapitelId: TutorialKapitelId) => void;
    /**
     * „Dazu fragen" — oeffnet den Chat `HilfeChat` mit diesem Kapitel im Ruecken
     * (Plan §1.8, Paket E). Fehlt der Rueckkanal, bleibt der Knopf sichtbar und
     * abgeschaltet: er kuendigt an, was kommt, und der Titel sagt warum.
     */
    onDazuFragen?: (kapitelId: TutorialKapitelId) => void;
    onAbschliessen: (kapitelId: TutorialKapitelId) => void;
}

/** Eine Sprungmarke aus `marken.json` (Plan §2.3, `videoLogik.kapitelmarken`). */
interface Kapitelmarke {
    schritt: number;
    startMs: number;
}

/** Symbol je Schrittart — zeigt auf einen Blick, ob man klicken oder nur schauen soll. */
const AKTION_SYMBOL: Record<TutorialAktion, React.ReactNode> = {
    klick: <MousePointerClick className="w-3.5 h-3.5" />,
    tippen: <Keyboard className="w-3.5 h-3.5" />,
    zeigen: <Eye className="w-3.5 h-3.5" />,
    warten: <Hourglass className="w-3.5 h-3.5" />,
};

const AKTION_WORT: Record<TutorialAktion, string> = {
    klick: 'Klicken',
    tippen: 'Eingeben',
    zeigen: 'Ansehen',
    warten: 'Warten',
};

const Abschnitt: React.FC<{ titel: string; icon: React.ReactNode; children: React.ReactNode }> = ({ titel, icon, children }) => (
    <section className="mb-8">
        <div className="flex items-center gap-3 mb-2.5 px-1">
            <h2 className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400 whitespace-nowrap flex items-center gap-1.5">
                <span className="text-slate-400">{icon}</span>{titel}
            </h2>
            <div className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent" />
        </div>
        {children}
    </section>
);

export const KapitelSeite: React.FC<Props> = ({
    kapitel, daten, stolperfallen, abgeschlossen, onZurueck, onStarteTour, onSelbstAusprobieren, onDazuFragen,
    onAbschliessen,
}) => {
    const inhalt = daten?.inhalt;
    const medien = daten?.medien;
    const screenshots = medien?.screenshots ?? [];

    // Register-Stolperfallen zuerst, dann die kuratierten aus der Markdown-Datei;
    // Dubletten fliegen raus, damit derselbe Satz nicht zweimal rot dasteht.
    const alleStolperfallen = Array.from(new Set([...(kapitel.stolperfallen ?? []), ...stolperfallen]));

    const praxisText = inhalt?.praxis;
    const faq = inhalt?.faq ?? [];

    // Kapitelmarken (Plan §2.3): eine eigene JSON-Datei neben dem Video, kein
    // Feld in `inhalt` — sie sind Zeitstempel im Videoschnitt, nicht Lernstoff.
    // Ohne `medien.marken` bleibt die Liste leer und es erscheint nichts.
    const videoRef = useRef<HTMLVideoElement>(null);
    const [kapitelmarken, setKapitelmarken] = useState<Kapitelmarke[]>([]);

    useEffect(() => {
        // Kein `setKapitelmarken([])` hier: `kapitelmarken` startet bereits auf
        // [], und ein synchrones setState im Effektrumpf erzeugt eine
        // Kaskadenrendierung (react-hooks/set-state-in-effect, CI-Ratsche) —
        // dieselbe Abwägung wie in `useLernzentrum.ts`. Die Seite wird beim
        // Kapitelwechsel ohnehin neu gemountet (`LernzentrumView`), ein Rest
        // vom vorigen Kapitel bleibt also nicht stehen.
        if (!medien?.marken) return;
        let abgebrochen = false;
        fetch(medien.marken)
            .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
            .then((json) => {
                if (abgebrochen || !Array.isArray(json)) return;
                const marken = json.filter(
                    (m): m is Kapitelmarke =>
                        m && typeof m.schritt === 'number' && typeof m.startMs === 'number',
                );
                setKapitelmarken(marken);
            })
            .catch(() => {
                // Marken sind eine Zugabe, kein Nachweis — ohne sie zeigt die
                // Seite einfach nur das Video ohne Sprungknöpfe.
            });
        return () => {
            abgebrochen = true;
        };
    }, [medien?.marken]);

    const springeZuSchritt = (startMs: number): void => {
        if (videoRef.current) videoRef.current.currentTime = startMs / 1000;
    };

    return (
        <div data-glass-view="lernzentrum" className="h-full overflow-y-auto bg-slate-50">
            <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 pb-24">

                {/* ── Kopf ── */}
                <button
                    type="button"
                    onClick={onZurueck}
                    className="flex items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors text-xs font-bold mb-4"
                >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span className="uppercase tracking-wider text-[11px]">Lernzentrum</span>
                </button>

                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <span className="text-[10px] font-black uppercase tracking-[0.18em] text-blue-500">
                        {BEREICH_LABEL[kapitel.bereich] ?? kapitel.bereich}
                    </span>
                    <span className="text-[11px] text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />{kapitel.dauerMin} Min.
                    </span>
                    {abgeschlossen && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-1.5 py-0.5">
                            <CheckCircle2 className="w-3 h-3" />Abgeschlossen
                        </span>
                    )}
                </div>
                <h1 className="text-2xl font-bold text-slate-800 leading-tight mb-5">{kapitel.titel}</h1>

                {/* ── Video oder Platzhalter ── */}
                {medien?.video ? (
                    <div className="rounded-2xl overflow-hidden border border-slate-200 bg-black mb-8">
                        {/* Untertitelspur nur, wenn die Pipeline sie erzeugt hat — ein
                            <track> ohne Datei laesst den Spieler die Schaltflaeche anbieten
                            und dann ins Leere greifen. */}
                        <video
                            ref={videoRef}
                            controls
                            preload="metadata"
                            poster={medien.poster}
                            src={medien.video}
                            className="w-full aspect-video bg-black"
                        >
                            {medien.vtt && <track kind="captions" srcLang="de" label="Deutsch" src={medien.vtt} default />}
                        </video>
                        {kapitelmarken.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 p-3 bg-slate-900 border-t border-slate-800">
                                {kapitelmarken.map((marke) => (
                                    <button
                                        key={marke.schritt}
                                        type="button"
                                        onClick={() => springeZuSchritt(marke.startMs)}
                                        className="text-[11px] font-semibold text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg px-2.5 py-1 transition-colors"
                                    >
                                        {marke.schritt}. {kapitel.schritte[marke.schritt - 1]?.stichwort ?? `Schritt ${marke.schritt}`}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-100/70 aspect-video max-h-64 flex flex-col items-center justify-center text-slate-400 mb-8">
                        <Video className="w-8 h-8 mb-2" />
                        <p className="text-sm font-bold text-slate-500">Video folgt</p>
                        <p className="text-[11px] mt-0.5">Die Anleitung darunter ist vollständig.</p>
                    </div>
                )}

                {/* ── Knopfleiste ── */}
                <div className="flex flex-wrap gap-2 mb-8">
                    <button
                        type="button"
                        onClick={() => onStarteTour(kapitel.id)}
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3.5 py-2 rounded-xl transition-colors"
                    >
                        <PlayCircle className="w-4 h-4" />Jetzt in der App zeigen
                    </button>
                    {onSelbstAusprobieren && (
                        <button
                            type="button"
                            onClick={() => onSelbstAusprobieren(kapitel.id)}
                            className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3.5 py-2 rounded-xl border border-slate-200 transition-colors"
                        >
                            <FlaskConical className="w-4 h-4 text-emerald-600" />Selbst ausprobieren
                        </button>
                    )}
                    {/* „Dazu fragen" oeffnet den geerdeten Chat (Plan §1.8, Paket E) mit
                        diesem Kapitel im Ruecken. Ohne Rueckkanal — etwa dort, wo der
                        Chat noch nicht verdrahtet ist — bleibt der Knopf sichtbar und
                        abgeschaltet: er kuendigt an, was kommt, und der Titel sagt warum. */}
                    <button
                        type="button"
                        onClick={onDazuFragen ? () => onDazuFragen(kapitel.id) : undefined}
                        disabled={!onDazuFragen}
                        title={onDazuFragen
                            ? 'Frag OS-IA zu diesem Kapitel'
                            : 'Der Lern-Chat kommt in einem der nächsten Schritte.'}
                        className={`inline-flex items-center gap-2 text-xs font-semibold px-3.5 py-2 rounded-xl border transition-colors ${onDazuFragen
                            ? 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200'
                            : 'bg-white text-slate-400 border-slate-200 cursor-not-allowed'}`}
                    >
                        <MessageCircleQuestion className={`w-4 h-4 ${onDazuFragen ? 'text-blue-600' : ''}`} />Dazu fragen
                    </button>
                </div>

                {/* ── Schritte ── */}
                <Abschnitt titel="Schritt für Schritt" icon={<MousePointerClick className="w-3.5 h-3.5" />}>
                    <ol className="space-y-3">
                        {kapitel.schritte.map((schritt, i) => {
                            const text = inhalt?.schritte?.[i];
                            const bild = screenshots[i];
                            return (
                                <li key={`${schritt.anker}-${i}`} className="bg-white rounded-2xl border border-slate-200 p-4 flex gap-3">
                                    <span className="shrink-0 w-7 h-7 rounded-full bg-blue-50 text-blue-700 text-xs font-bold flex items-center justify-center border border-blue-100">
                                        {i + 1}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                                            {AKTION_SYMBOL[schritt.aktion]}
                                            <span>{AKTION_WORT[schritt.aktion]}</span>
                                            {schritt.wert && <span className="normal-case tracking-normal text-slate-500 font-mono">„{schritt.wert}"</span>}
                                        </div>
                                        <p className="text-sm text-slate-700 leading-relaxed">{text || schritt.stichwort}</p>
                                        {bild && (
                                            <img
                                                src={bild}
                                                alt={`Schritt ${i + 1}: ${schritt.stichwort}`}
                                                loading="lazy"
                                                className="mt-3 rounded-xl border border-slate-200 w-full"
                                            />
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </Abschnitt>

                {/* ── Praxisbeispiel ── */}
                <Abschnitt titel="Praxisbeispiel" icon={<Lightbulb className="w-3.5 h-3.5" />}>
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                        {praxisText ? (
                            <p className="text-sm text-amber-900 leading-relaxed whitespace-pre-line">{praxisText}</p>
                        ) : (
                            <>
                                <p className="text-sm text-amber-900 leading-relaxed"><span className="font-semibold">Situation: </span>{kapitel.praxis.kontext}</p>
                                <p className="text-sm text-amber-900 leading-relaxed mt-1.5"><span className="font-semibold">Ziel: </span>{kapitel.praxis.ziel}</p>
                            </>
                        )}
                    </div>
                </Abschnitt>

                {/* ── Stolperfallen ── */}
                {alleStolperfallen.length > 0 && (
                    <Abschnitt titel="Stolperfallen" icon={<AlertTriangle className="w-3.5 h-3.5" />}>
                        <ul className="bg-red-50 border border-red-200 rounded-2xl p-4 space-y-2.5">
                            {alleStolperfallen.map((falle, i) => (
                                <li key={i} className="flex gap-2.5 text-sm text-red-900 leading-relaxed">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-red-500" />
                                    {/* Der kuratierte Text traegt Markdown-Fettung (**…**) fuer die
                                        Kurzfassung. Sie wird hier NICHT gerendert, sondern entfernt:
                                        ein Markdown-Renderer waere die einzige Stelle in dieser App,
                                        die fremden Text als Auszeichnung interpretiert. */}
                                    <span>{falle.replace(/\*\*/g, '')}</span>
                                </li>
                            ))}
                        </ul>
                    </Abschnitt>
                )}

                {/* ── FAQ ── */}
                {faq.length > 0 && (
                    <Abschnitt titel="Häufige Fragen" icon={<HelpCircle className="w-3.5 h-3.5" />}>
                        <div className="space-y-2">
                            {faq.map((eintrag, i) => (
                                <details key={i} className="bg-white rounded-2xl border border-slate-200 p-4 group">
                                    <summary className="text-sm font-semibold text-slate-700 cursor-pointer list-none flex items-center gap-2">
                                        <HelpCircle className="w-4 h-4 text-slate-400 shrink-0" />
                                        {eintrag.frage}
                                    </summary>
                                    <p className="text-sm text-slate-600 leading-relaxed mt-2 pl-6">{eintrag.antwort}</p>
                                </details>
                            ))}
                        </div>
                    </Abschnitt>
                )}

                {/* ── Abschluss ── */}
                <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p className="text-sm font-bold text-slate-700">{abgeschlossen ? 'Kapitel ist abgeschlossen' : 'Alles klar?'}</p>
                        <p className="text-[11px] text-slate-500">
                            {abgeschlossen ? 'Du kannst jederzeit zurückkommen.' : 'Der Haken zählt in deinen Lernpfad — mehr passiert nicht.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => onAbschliessen(kapitel.id)}
                        disabled={abgeschlossen}
                        className={`inline-flex items-center gap-2 text-xs font-semibold px-3.5 py-2 rounded-xl border transition-colors ${abgeschlossen
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 cursor-default'
                            : 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600'}`}
                    >
                        <CheckCircle2 className="w-4 h-4" />{abgeschlossen ? 'Abgeschlossen' : 'Kapitel abschließen'}
                    </button>
                </div>
            </div>
        </div>
    );
};
